import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageBySlug } from '../lib/cms';
import { useSEO } from '../hooks/useSEO';

export default function Bestilling() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const page = await getPageBySlug('bestilling');
      setPageData(page);
      setLoading(false);
    }
    loadData();
  }, []);

  useSEO({
    title: pageData?.meta_title || 'Bestilling · Vissenbjerg Bageri',
    description: pageData?.meta_description || 'Bestil friskbagt surdejsbrød, stenovnsbrød, rundstykker, kagemand og lagkager hos Vissenbjerg Bageri. Ring på 64471266 eller send os en besked.'
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-cream">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold-brown border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-transparent text-chocolate py-12 md:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-brown">
            Forudbestilling
          </span>
          <h1 className="font-serif text-4xl font-bold sm:text-5xl text-chocolate">
            Bestilling
          </h1>
          <div className="h-1 w-20 bg-gold-brown mx-auto rounded-full" />
        </div>

        {/* Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="space-y-6">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate">
              Bestil friskbagt brød og lækre kager
            </h2>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Hos Vissenbjerg Bageri gør vi det nemt for dig at bestille til dine fester, møder eller blot til morgenbordet. Du kan forudbestille alle vores lækre surdejsbrød, stenovnsbrød, rundstykker, kagemænd, lagkager og meget mere.
            </p>
            <div className="bg-chocolate/5 border-l-4 border-gold-brown p-5 rounded-r-xl">
              <p className="text-sm text-chocolate/90 italic">
                "Bemærk venligst, at specielle konditorkager og større bestillinger gerne skal bestilles i god tid, så vores konditor og bager kan kræse om detaljerne."
              </p>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gold-brown/10 h-[350px]">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/img-9315.jpg"
              alt="Bestilling af brød og kager"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* How to order cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white p-8 rounded-2xl shadow-md border border-gold-brown/10 space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="h-12 w-12 rounded-full bg-gold-brown/10 flex items-center justify-center text-gold-brown text-xl">
                <i className="fa-solid fa-phone" />
              </div>
              <h3 className="font-serif text-xl font-bold text-chocolate">Ring til os</h3>
              <p className="text-sm text-chocolate/70 leading-relaxed">
                Den hurtigste og sikreste måde at bestille på er ved at ringe direkte til butikken. Så kan vi med det samme bekræfte din bestilling og aftale afhentningstidspunkt.
              </p>
            </div>
            <div className="pt-6">
              <a
                href="tel:64471266"
                className="inline-flex items-center gap-2 text-gold-brown font-bold hover:text-copper transition-colors"
              >
                Ring på 64 47 12 66 <i className="fa-solid fa-arrow-right" />
              </a>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-md border border-gold-brown/10 space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="h-12 w-12 rounded-full bg-gold-brown/10 flex items-center justify-center text-gold-brown text-xl">
                <i className="fa-solid fa-envelope" />
              </div>
              <h3 className="font-serif text-xl font-bold text-chocolate">Send en besked</h3>
              <p className="text-sm text-chocolate/70 leading-relaxed">
                Du kan også sende os en forespørgsel via vores kontaktformular. Vi besvarer din henvendelse så hurtigt som muligt, men bemærk at bestillingen først er gældende, når du har modtaget en bekræftelse.
              </p>
            </div>
            <div className="pt-6">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 text-gold-brown font-bold hover:text-copper transition-colors"
              >
                Gå til kontaktformular <i className="fa-solid fa-arrow-right" />
              </Link>
            </div>
          </div>
        </div>

        {/* Brunsvigermand.dk Special Order Box */}
        <div className="rounded-2xl bg-chocolate text-cream p-8 md:p-10 shadow-xl border border-gold-brown/20 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-brown">
              Specialbestilling
            </span>
            <h3 className="font-serif text-2xl font-bold text-cream">
              Skal du bestille brunsviger kagemand eller kagedame?
            </h3>
            <p className="text-sm text-cream/80 max-w-2xl">
              Bestil nemt og hurtigt online via vores dedikerede side på brunsvigermand.dk med pynt, slik og personlig tekst.
            </p>
          </div>
          <div className="flex-shrink-0">
            <a
              href="https://brunsvigermand.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-gold-brown px-6 py-3 text-sm font-semibold text-cream hover:bg-copper transition-colors duration-200 shadow-lg"
            >
              <span>Bestil på brunsvigermand.dk</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
